import { PRODUCT_IDS, getProductContext } from "./productContext";
import {
  buildPublicLocalizedPath,
  getLocaleMeta,
  normalizeLocale,
  resolveAgentDashboardBase,
} from "./routeLocale";
import {
  DASHBOARD_MENU_SEGMENT_TO_ROUTE_ID,
  getRouteDefinition,
} from "./routeRegistry";
import { isCapabilityEnabled, resolveCapabilities } from "./capabilityPolicies";
import { isFeatureAllowedForLocale } from "./languageFeatures";
import { traceNonHebrewRouteBlock, traceRouteResolve } from "./routeTelemetry";

function joinBaseAndSegment(base, segment) {
  const normalizedBase = String(base || "").replace(/\/+$/, "");
  const normalizedSegment = String(segment || "").replace(/^\/+/, "");
  if (!normalizedSegment) return normalizedBase || "/";
  return `${normalizedBase}/${normalizedSegment}`;
}

/** Replace :param placeholders in a route segment (supports :hotelId and :hotelid). */
export function applyRouteParams(segment, params = {}) {
  if (!segment || !params || typeof params !== "object") return segment;
  let out = String(segment);
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    const encoded = encodeURIComponent(String(value));
    out = out.replace(new RegExp(`:${key}\\b`, "gi"), encoded);
  }
  return out;
}

function resolveRouteSegment(definition, locale, useLegacyAlias) {
  if (useLegacyAlias && definition.legacyPaths?.[locale]) {
    return definition.legacyPaths[locale];
  }
  return definition.paths?.[locale];
}

/**
 * Resolve full pathname from ROUTE_REGISTRY (single SSOT — no separate path map).
 *
 * @param {string} routeId
 * @param {{ locale?: string, product?: string, useLegacyAlias?: boolean, entitlements?: object, session?: object }} ctx
 * @returns {{ path: string, legacyAliasUsed: boolean, routeId: string, product: string, locale: string } | null}
 */
export function resolveRoutePath(routeId, ctx = {}) {
  const definition = getRouteDefinition(routeId);
  if (!definition) {
    traceRouteResolve({ routeId, error: "unknown_route_id" });
    return null;
  }

  const locale = normalizeLocale(ctx.locale ?? "he");
  const product = ctx.product ?? definition.product;
  const useLegacyAlias = ctx.useLegacyAlias !== false;

  const absolutePath = definition.absolutePaths?.[locale];
  if (absolutePath) {
    const legacyAliasUsed = absolutePath.startsWith("/p/");
    const result = {
      path: absolutePath,
      legacyAliasUsed,
      routeId,
      product,
      locale,
      namespace: definition.namespace,
      capability: definition.capability,
      feature: definition.feature,
    };
    traceRouteResolve({
      routeId,
      product,
      locale,
      resolvedPath: absolutePath,
      legacyAliasUsed,
    });
    return result;
  }

  const segment = resolveRouteSegment(definition, locale, useLegacyAlias);

  if (segment == null || segment === undefined) {
    traceRouteResolve({ routeId, locale, product, error: "missing_path_segment" });
    return null;
  }

  const params = ctx.params && typeof ctx.params === "object" ? ctx.params : {};
  const resolvedSegment = applyRouteParams(segment, params);

  let path;
  let legacyAliasUsed = false;

  if (product === PRODUCT_IDS.AGENT) {
    const base = resolveAgentDashboardBase(locale, { useLegacyAlias });
    legacyAliasUsed = locale === "he" && useLegacyAlias;
    path = joinBaseAndSegment(base, resolvedSegment);
  } else if (product === PRODUCT_IDS.PUBLIC) {
    const prefix = getLocaleMeta(locale).publicPrefix;
    if (!resolvedSegment) {
      path = buildPublicLocalizedPath("/", prefix);
    } else {
      path = buildPublicLocalizedPath(`/${resolvedSegment}`, prefix);
    }
  } else if (product === PRODUCT_IDS.ADMIN) {
    const adminBase = getProductContext(PRODUCT_IDS.ADMIN).dashboardBase || "/administration";
    path = segment ? joinBaseAndSegment(adminBase, resolvedSegment) : adminBase;
  } else {
    traceRouteResolve({ routeId, locale, product, error: "unsupported_product" });
    return null;
  }

  const result = {
    path,
    legacyAliasUsed,
    routeId,
    product,
    locale,
    namespace: definition.namespace,
    capability: definition.capability,
    feature: definition.feature,
  };

  traceRouteResolve({
    routeId,
    product,
    locale,
    resolvedPath: path,
    legacyAliasUsed,
  });

  return result;
}

/**
 * Dashboard-relative path (e.g. orders/packages, payment/:id) under resolved base.
 */
export function resolveDashboardRelativePath(relativePath, ctx = {}) {
  const locale = normalizeLocale(ctx.locale ?? "he");
  const useLegacyAlias = ctx.useLegacyAlias !== false;
  const base = resolveAgentDashboardBase(locale, { useLegacyAlias });
  const path = joinBaseAndSegment(base, relativePath);
  const result = {
    path,
    legacyAliasUsed: locale === "he" && useLegacyAlias,
    routeId: null,
    product: PRODUCT_IDS.AGENT,
    locale,
  };
  traceRouteResolve({
    routeId: null,
    menuSegment: relativePath,
    product: PRODUCT_IDS.AGENT,
    locale,
    resolvedPath: path,
    legacyAliasUsed: result.legacyAliasUsed,
  });
  return result;
}

/**
 * Resolve SideNav / menu path via registry lookup or relative fallback.
 */
export function resolveDashboardMenuPath(menuPath, ctx = {}) {
  const raw = String(menuPath || "");
  const [pathPart, queryPart] = raw.split("?");
  const routeId = DASHBOARD_MENU_SEGMENT_TO_ROUTE_ID[pathPart];
  const resolved = routeId
    ? resolveRoutePath(routeId, ctx)
    : resolveDashboardRelativePath(pathPart, ctx);

  if (!resolved?.path) return null;
  if (queryPart) {
    return { ...resolved, path: `${resolved.path}?${queryPart}` };
  }
  return resolved;
}

/**
 * @param {string} routeId
 * @param {{ locale?: string, product?: string, entitlements?: object, session?: object }} ctx
 * @returns {{ allowed: boolean, reason?: string, capabilities?: object }}
 */
export function canActivateRoute(routeId, ctx = {}) {
  const definition = getRouteDefinition(routeId);
  if (!definition) {
    return { allowed: false, reason: "unknown_route" };
  }

  const locale = normalizeLocale(ctx.locale ?? "he");
  const product = ctx.product ?? definition.product;
  const capabilities = resolveCapabilities({
    locale,
    product,
    entitlements: ctx.entitlements,
    session: ctx.session,
  });

  const hasPath =
    definition.absolutePaths?.[locale] != null ||
    definition.paths?.[locale] != null ||
    definition.legacyPaths?.[locale] != null;

  if (!hasPath) {
    return { allowed: false, reason: "locale_not_supported", capabilities };
  }

  if (!isFeatureAllowedForLocale(locale, definition.feature)) {
    traceNonHebrewRouteBlock({
      locale,
      feature: definition.feature,
      routeId,
      reason: "feature_not_available",
    });
    return { allowed: false, reason: "feature_not_available", capabilities };
  }

  if (!isCapabilityEnabled(capabilities, definition.capability)) {
    return { allowed: false, reason: "capability_denied", capabilities };
  }

  return { allowed: true, capabilities };
}

export function resolveRoutePathOrThrow(routeId, ctx = {}) {
  const resolved = resolveRoutePath(routeId, ctx);
  if (!resolved) {
    throw new Error(`Unable to resolve route path for ${routeId}`);
  }
  return resolved;
}
